import React, { useEffect, useContext } from 'react';
import useDescuentosJerarquicos from './hooks/useDescuentosJerarquicos';
import { useSucursal } from '../contexts/SucursalContext'; // Importar contexto de sucursal

/**
 * Componente para gestionar los descuentos en la orden de trabajo
 * Implementa la lógica completa del sistema de descuentos
 */  const GestorDescuentos = ({
  origen = '',
  medicoSeleccionado = '',
  clienteSeleccionado = null,
  medioPago = '',
  fechaCotizacion = '',
  analisisAgregados = [],
  setAnalisisAgregados = () => {},
  setDescuentoGlobal = () => {},
  setDescuentoGlobalRemitido = () => {},
  setDescuentoAsegurado = () => {},
  calcularTotales = () => {},
  valorCopago = 0,
  server = '',
  perfilSeleccionado = null, // Objeto de perfil seleccionado
  prioridadDescuento = [] // Configuraciones de prioridad de descuento desde la BD
}) => {
  // Obtener la sucursal seleccionada del contexto
  const { sucursalSeleccionada } = useSucursal();
  
  // Cargar las configuraciones de prioridad de descuentos
  useEffect(() => {
    // Verificar si ya tenemos las prioridades cargadas
    if (prioridadDescuento.length === 0) {
      const fetchPrioridadesDescuento = async () => {
        try {
          console.log('🔍 Obteniendo configuración de prioridades de descuentos...');
          const response = await fetch(`${server}/conf_prioridad_descuento`);
          
          if (!response.ok) {
            throw new Error(`Error al obtener prioridades: ${response.status}`);
          }
          
          const data = await response.json();
          const prioridades = data.$values || [];
          
          console.log('✅ Prioridades de descuento obtenidas:', prioridades);
          // Ordenar por orden de prioridad (si existe ese campo)
          const prioridadesOrdenadas = prioridades.sort((a, b) => 
            (a.orden || 0) - (b.orden || 0)
          );
          
          // Actualizar en el componente padre
          if (typeof window.setPrioridadDescuento === 'function') {
            window.setPrioridadDescuento(prioridadesOrdenadas);
          }
        } catch (error) {
          console.error('❌ Error al cargar las prioridades de descuento:', error);
        }
      };
      
      // Ejecutar la carga
      fetchPrioridadesDescuento();
    } else {
      console.log('✅ Prioridades de descuento ya cargadas:', prioridadDescuento);
    }
  }, [server, prioridadDescuento]);
  
  // Usar el hook de descuentos jerárquicos
  const {
    calcularDescuentoJerarquico,
    mostrarResumenDescuentos,
    descuentosFuente,
    obtenerDescuentoPorPerfil,
    recalcularTodosLosAnalisis
  } = useDescuentosJerarquicos({
    origen,
    medicoSeleccionado,
    clienteSeleccionado,
    medioPago,
    fechaCotizacion,
    analisisAgregados,
    setAnalisisAgregados,
    setDescuentoGlobal,
    setDescuentoGlobalRemitido,
    setDescuentoAsegurado,
    calcularTotales,
    valorCopago,
    server,
    perfilSeleccionado,
    sucursalSeleccionada
  });

  // Ejecutar cálculo de descuento cuando cambian las dependencias
  useEffect(() => {
    console.log('🔄 Recalculando descuentos por cambio en dependencias');
    
    // Debug - Inspeccionar el perfil seleccionado detalladamente
    if (perfilSeleccionado === null) {
      console.log('⚠️ Perfil seleccionado es NULL');
    } else if (perfilSeleccionado === '') {
      console.log('⚠️ Perfil seleccionado es una cadena vacía');
    } else if (typeof perfilSeleccionado !== 'object') {
      console.log(`⚠️ Perfil seleccionado no es un objeto, es: ${typeof perfilSeleccionado}`, perfilSeleccionado);
    } else {
      console.log('✅ Perfil seleccionado parece válido:', {
        id: perfilSeleccionado.idPerfil,
        descripcion: perfilSeleccionado.descripcionPerfil,
        porcentaje: perfilSeleccionado.porcentaje,
        tieneDescuento: perfilSeleccionado.porcentaje !== undefined && perfilSeleccionado.porcentaje !== null && perfilSeleccionado.porcentaje > 0
      });
    }
    
    console.log('Debug - Props recibidas:', { 
      medicoSeleccionado, 
      origen, 
      clienteSeleccionado,
      medioPago,
      perfilSeleccionado,
      fechaCotizacion,
      analisisAgregados: analisisAgregados?.length || 0,
      prioridadDescuento
    });
    
    // Solo calcular si tenemos los datos necesarios
    if (origen || medicoSeleccionado || clienteSeleccionado || medioPago || perfilSeleccionado) {
      calcularDescuentoJerarquico();
    }
  }, [
    medicoSeleccionado, 
    origen, 
    clienteSeleccionado, 
    medioPago,
    perfilSeleccionado,  // Agregar perfil como dependencia
    fechaCotizacion
  ]);

  // Mostrar información de debugging cuando cambia el descuento
  useEffect(() => {
    if (descuentosFuente.fuente !== 'ninguno') {
      console.log(`💡 Descuento aplicado: ${descuentosFuente.descuentoNormal}% (${descuentosFuente.fuente})`);
      if (descuentosFuente.descuentoRemitido > 0) {
        console.log(`💡 Descuento remitido: ${descuentosFuente.descuentoRemitido}%`);
      }
    }
  }, [descuentosFuente]);
  
  // Gestionar específicamente los cambios en el perfil
  useEffect(() => {
    // 🛡️ PROTECCIÓN: NO RECALCULAR SI TODOS LOS ANÁLISIS SON DEL BACKEND
    const todosDelBackend = analisisAgregados.length > 0 && 
                           analisisAgregados.every(a => a.descuentoDelBackend === true);
    
    if (todosDelBackend) {
      console.log('🛡️ PROTECCIÓN GestorDescuentos: Todos los análisis son del backend - NO RECALCULAR por cambio de perfil');
      return;
    }
    
    // Primero registramos en el log información detallada sobre el perfil
    console.log('🔍 Perfil seleccionado actualizado (GestorDescuentos):', perfilSeleccionado);
    console.log('🔍 Tipo de perfilSeleccionado:', typeof perfilSeleccionado);
    
    if (perfilSeleccionado && perfilSeleccionado !== '') {
      // Mostrar todas las propiedades del objeto perfil para debugging
      console.log('🔍 Propiedades del perfil:', Object.keys(perfilSeleccionado).map(key => ({
        propiedad: key,
        valor: perfilSeleccionado[key]
      })));
      
      // Verificar que perfilSeleccionado es un objeto con las propiedades necesarias
      if (
        typeof perfilSeleccionado === 'object' && 
        perfilSeleccionado !== null &&
        perfilSeleccionado.porcentaje !== undefined && 
        perfilSeleccionado.porcentaje !== null &&
        perfilSeleccionado.porcentaje > 0
      ) {
        console.log('✅ Descuento disponible del perfil:', perfilSeleccionado.porcentaje);
        console.log('ℹ️ Este descuento se aplicará SÓLO a los análisis pertenecientes a este perfil');
        
        // El descuento del perfil se aplicará específicamente a los análisis de este perfil
        // en la función recalcularTodosLosAnalisis
        
        console.log('🔄 Forzando recálculo de análisis por perfil seleccionado con descuento');
        console.log('   - Perfil:', perfilSeleccionado.descripcionPerfil);
        console.log('   - Descuento:', perfilSeleccionado.porcentaje);
        console.log('   - Total análisis antes del recálculo:', analisisAgregados.length);
        
        // Forzar recálculo para aplicar descuentos específicos de perfil
        // Pasamos la configuración de prioridades si está disponible
        console.log('🔍 Prioridad de descuentos configurada:', prioridadDescuento);
        
        recalcularTodosLosAnalisis(prioridadDescuento)
          .then(nuevosAnalisis => {
            if (nuevosAnalisis && nuevosAnalisis.length > 0) {
              console.log('✅ Recálculo exitoso - Total análisis:', nuevosAnalisis.length);
              
              // Resumen de descuentos aplicados
              const resumenDescuentos = nuevosAnalisis.map(analisis => ({
                nombre: analisis.analisis,
                descuento: `${analisis.porcentajeDescuento || 0}%`,
                fuente: analisis.fuenteDescuento || 'ninguna',
                total: analisis.total
              }));
              
              console.table(resumenDescuentos);
              
              setAnalisisAgregados(nuevosAnalisis);
              calcularTotales(nuevosAnalisis);
            } else {
              console.warn('⚠️ El recálculo no retornó ningún análisis');
            }
          })
          .catch(error => {
            console.error('❌ Error al recalcular análisis:', error);
          });
      }
    } else {
      console.log('ℹ️ No hay perfil seleccionado actualmente');
    }
  }, [perfilSeleccionado]);

  // Este componente no renderiza nada visible, solo maneja lógica
  return null;
};

export default GestorDescuentos;
